import {
    ApiHandler,
    appQueryClient,
    EnabledFeature,
    PrimaryRepresentativeFormData,
    DONEE_QUERY_KEY,
    DoneeQueryData,
    doneeQueryFn,
} from '@givelify/api';
import {
    Logger,
    TimezoneManager,
    addProfile,
    getAxiosClient,
    makeApiRequest,
    setDefaultStartDate,
} from '@givelify/utils';
import { ThunkAction } from 'redux-thunk';
import { setDoneeAndCampuses } from 'store/donee/actions';
import { loadDoneeNotifications } from 'store/doneeNotification/thunks';
import { getScheduledReport } from 'store/integration/thunks';
import { loadNotifications } from 'store/notifications/thunks';
import { loadUser } from 'store/user/thunks';
import accessibleFeaturesByRole from 'utils/accessibleFeaturesByRole';
import { Features, isFeatureEnabled } from 'utils/featureGating';
import { webConfig } from 'webConfig';
import { AppActions, AppState } from '..';
import { getIntegration } from '../automatedIntegration/thunks';
import {
    setEnabledFeatures,
    setSnapToGiveQRCodeURL,
    setOnlineGivingURLs,
    setIncompleteDoneePrimaryRep,
} from './actions';
import {
    SET_SNAP_TO_GIVE_QR_CODE_URL,
    SET_ONLINE_GIVING_URLS,
    OnlineGivingURLsInfo,
    SanpToGiveResponse,
} from './types';

/* eslint-disable-next-line */

export const locationRetrieveError = '( Unable to Retrieve Location )';

export const loadEnabledFeatures =
    (
        role: string,
        fingerprint?: string,
    ): ThunkAction<
        Promise<EnabledFeature[]>,
        AppState,
        undefined,
        AppActions
    > =>
    async (dispatch) => {
        const enabledFeaturesResponse =
            await ApiHandler.instance.common.getEnabledFeatures(fingerprint);
        if (!enabledFeaturesResponse.success) return null;

        const features = enabledFeaturesResponse.response.enabledFeatures;
        const roledFeatures = features?.map((feature) => ({
            ...feature,
            isEnabled: role
                ? feature.isEnabled &&
                  accessibleFeaturesByRole[role]?.includes(feature.label)
                : feature.isEnabled,
        }));
        dispatch(setEnabledFeatures(roledFeatures));
        return roledFeatures;
    };

export const loadSnapGiveQRCodes = (doneeId: number) => async (dispatch) => {
    const httpRequest = getAxiosClient().get(`/donees/${doneeId}/snap-to-give`);

    const qrCodesResponse = await makeApiRequest<SanpToGiveResponse>(
        httpRequest,
    );

    if (!qrCodesResponse.success) return null;

    if (qrCodesResponse.response.status === 'error')
        dispatch({ type: SET_SNAP_TO_GIVE_QR_CODE_URL, url: '' });
    else dispatch(setSnapToGiveQRCodeURL(qrCodesResponse.response));

    return qrCodesResponse.response;
};

export const loadOnlineGivingURLs = (doneeId: number) => async (dispatch) => {
    const httpRequest = getAxiosClient().get(`/donees/${doneeId}/giving-urls`);

    const givingURLsResponse = await makeApiRequest<{
        onlineGivingURLs: OnlineGivingURLsInfo[];
    }>(httpRequest);

    if (!givingURLsResponse.success) {
        dispatch({ type: SET_ONLINE_GIVING_URLS, onlineGivingURLs: [] });

        // We won't prevent the app from loading if the Giving Url server is unavailable
        // An URL from the Donee model will be used instead.
        return true;
    }
    dispatch(setOnlineGivingURLs(givingURLsResponse.response));

    return givingURLsResponse.response;
};

export const loadIncompletePrimaryRepData =
    (): ThunkAction<
        Promise<{
            success: boolean;
            result: PrimaryRepresentativeFormData | null;
        }>,
        AppState,
        undefined,
        AppActions
    > =>
    async (dispatch) => {
        const incompletePrimaryRepresentative =
            await ApiHandler.instance.common.getIncompletePrimaryRepresentative();

        if (!incompletePrimaryRepresentative.success) {
            dispatch(setIncompleteDoneePrimaryRep({}));
            return { success: true, result: null };
        }

        try {
            const parsedPrimaryRepFormData: PrimaryRepresentativeFormData =
                JSON.parse(
                    incompletePrimaryRepresentative.response.data.formData,
                );

            dispatch(setIncompleteDoneePrimaryRep(parsedPrimaryRepFormData));

            return { success: true, result: parsedPrimaryRepFormData };
        } catch {
            return { success: false, result: null };
        }
    };

const loadDoneeInternal = async (defaultDoneeId?: number) => {
    // Use fetchQuery so React Query manages the request and deduplicates
    // with DoneeProvider's useDoneeQuery (which may fire concurrently via defer)
    let data: DoneeQueryData | undefined;
    try {
        data = await appQueryClient.fetchQuery({
            queryKey: [DONEE_QUERY_KEY],
            queryFn: doneeQueryFn,
        });
    } catch (error) {
        Logger.log(
            'Failed to load donee data',
            { error: String(error) },
            Logger.LogLevel.ERROR,
        );
        return null;
    }
    if (!data?.campuses) return null;
    let donee = data.campuses[0];
    if (defaultDoneeId && defaultDoneeId > 0) {
        const defaultDonee = data.campuses.find((c) => c.id === defaultDoneeId);
        if (defaultDonee) {
            donee = defaultDonee;
        }
    }
    return { donee, campuses: data.campuses };
};

/**
 * Call this method once when app loads
 */
export const loadData =
    (
        defaultDoneeId?: number,
    ): ThunkAction<Promise<boolean>, AppState, undefined, AppActions> =>
    async (dispatch, getState) => {
        const user = getState().User?.user;
        const fingerprint = getState().User.fingerprint;

        const userRequest = dispatch(loadUser());
        const doneeRequest = loadDoneeInternal(defaultDoneeId);
        const incompletePrimaryRepRequest = dispatch(
            loadIncompletePrimaryRepData(),
        );
        const snapGiveRequest = dispatch(loadSnapGiveQRCodes(user.doneeId));
        const onlineGivingURLsRequest = dispatch(
            loadOnlineGivingURLs(user.doneeId),
        );
        const reportRequest = dispatch(getScheduledReport(user.doneeId));
        const enabledFeaturesRequest = dispatch(
            loadEnabledFeatures(user.role, fingerprint),
        );
        const integrationRequest = dispatch(getIntegration(user.doneeId));
        const notificationsRequest = dispatch(loadNotifications());
        const doneeNotificationRequest = dispatch(
            loadDoneeNotifications(defaultDoneeId || user.doneeId),
        );
        const seedsBalanceRequest = ApiHandler.instance.points.getPointsBalance(
            user.doneeId,
        );
        const seedsThresholdRequest =
            ApiHandler.instance.points.getPointsSettings(user.doneeId);

        const [
            userResult,
            doneeResult,
            incompletePrimaryRepResult,
            reportResult,
            integrationResult,
            enabledFeaturesResult,
            snapGiveResult,
            onlineGivingURLsResult,
            notificationsResult,
            doneeNotificationResult,
            seedsBalanceResult,
            seedsThresholdResult,
        ] = await Promise.all([
            userRequest,
            doneeRequest,
            incompletePrimaryRepRequest,
            reportRequest,
            integrationRequest,
            enabledFeaturesRequest,
            snapGiveRequest,
            onlineGivingURLsRequest,
            notificationsRequest,
            doneeNotificationRequest,
            seedsBalanceRequest,
            seedsThresholdRequest,
        ]);

        // Bridge: populate Redux Donee state for un-migrated consumers.
        // setDonneOnboardingStatus is NOT dispatched here — CampusChangeHandler's
        // useLayoutEffect bridge handles it, avoiding a duplicate getDonors API call.
        if (doneeResult) {
            setDefaultStartDate(doneeResult.donee.createdAt.toString());
            dispatch(
                setDoneeAndCampuses(doneeResult.donee, doneeResult.campuses),
            );
        }

        if (
            doneeResult &&
            defaultDoneeId &&
            doneeResult.donee.id !== defaultDoneeId
        ) {
            await dispatch(loadDoneeNotifications(doneeResult.donee.id));
        }

        const { donee } = doneeResult || {};

        const { isSuper } = user;
        if (!isSuper && donee) {
            addProfile(
                user,
                donee,
                enabledFeaturesResult,
                webConfig.appVersion,
            );
        }

        if (doneeResult !== null && enabledFeaturesResult !== null) {
            const timezonesEnabled = isFeatureEnabled(
                enabledFeaturesResult,
                Features.TIMEZONES,
                false,
            );
            const timezone = donee?.timezone;
            if (
                timezonesEnabled &&
                timezone !== undefined &&
                timezone !== null
            ) {
                TimezoneManager.setDefault(timezone);
            }
        }

        if (seedsBalanceResult && seedsBalanceResult.success) {
            appQueryClient.setQueryData(
                [
                    ApiHandler.instance.points.getPointsBalance.name,
                    user.doneeId,
                ],
                seedsBalanceResult.response,
            );
        } else {
            appQueryClient.setQueryData(
                [
                    ApiHandler.instance.points.getPointsBalance.name,
                    user.doneeId,
                ],
                {
                    totalPoints: 0,
                    availablePoints: 0,
                    redeemedPoints: 0,
                },
            );
        }

        if (seedsThresholdResult && seedsThresholdResult.success) {
            appQueryClient.setQueryData(
                [
                    ApiHandler.instance.points.getPointsSettings.name,
                    user.doneeId,
                ],
                seedsThresholdResult,
            );
        }

        return (
            userResult &&
            doneeResult !== null &&
            incompletePrimaryRepResult.success &&
            reportResult &&
            integrationResult &&
            enabledFeaturesResult &&
            !!snapGiveResult &&
            !!onlineGivingURLsResult &&
            notificationsResult &&
            doneeNotificationResult
        );
    };

/**
 * @deprecated Use URL navigation and DoneeProvider will react to changes
 */
export const changeCampus =
    (
        doneeId: number,
    ): ThunkAction<Promise<void>, AppState, undefined, AppActions> =>
    async (dispatch) => {
        await Promise.all([
            dispatch(loadDoneeNotifications(doneeId)),
            dispatch(loadSnapGiveQRCodes(doneeId)),
        ]);
    };
